<!--
  - Copyright (c) 2025.  Chad and contributors.
  - All rights reserved.
  -->

<template>
  <div>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
      <div class="md:col-span-1">
        <div class="setting-section-description">
          <FeatureTitle feature-basic feature-premium feature-standard title="SEO"/>
          <div class="feature-description">
            The page title defines the document's title that is shown in a browser's title bar or a page's
            tab.<br/><br/>

            The meta description is a brief summary of the page, often displayed in search engine results by providers
            like Google or Bing.
            <TheHelpLink
              url="https://help.fleetwire.io/en/article/online-reservations-general-1bmnifn/?bust=1645034418601"
            />
          </div>
        </div>
      </div>

      <div class="col-span-1">
        <v-card class="mb-5 py-4 md:py-10">
          <v-card-text>
            <div class="mb-5">
              <div class="font-bold">Page title</div>

              <v-text-field
                ref="page_title"
                v-model="settings['settings.seo.page_title']"
                :error-messages="companyMetaForm.errors.get('settings.seo.page_title')"
                :placeholder="companyName"
                counter="60"
                density="comfortable"
                hint="Keep your page title under 60 characters for best results."
                persistent-counter
                persistent-hint
                title="Page title"
                variant="outlined"
                @blur=" updateDefaultMeta('settings.seo.page_title', settings['settings.seo.page_title'])"
                @keyup="companyMetaForm.errors.clear('settings.seo.page_title')"
                @keydown.enter="updateDefaultMeta('settings.seo.page_title', settings['settings.seo.page_title']); $refs.page_title.$el.querySelector('input').blur()"
              />
            </div>

            <div class="mb-5">
              <div class="font-bold">Meta description</div>

              <v-text-field
                ref="meta_description"
                v-model="settings['settings.seo.meta_description']"
                :error-messages="companyMetaForm.errors.get('settings.seo.meta_description')"
                counter="160"
                density="comfortable"
                hint="Keep your meta description under 160 characters for optimal display."
                persistent-counter
                persistent-hint
                variant="outlined"
                @blur=" updateDefaultMeta('settings.seo.meta_description', settings['settings.seo.meta_description'])"
                @keyup="companyMetaForm.errors.clear('settings.seo.meta_description')"
                @keydown.enter="updateDefaultMeta('settings.seo.meta_description', settings['settings.seo.meta_description']); $refs.meta_description.$el.querySelector('input').blur()"
              />
            </div>

            <div class="w-100 max-w-[600px] border border-solid border-[#e0e0e0] rounded-lg mt-4 mb-5">
              <h3 class="border-b p-4 text-xs font-bold font-sans">Search Engine Preview</h3>
              <div class="p-4">
                <a
                  :href="shopUrl"
                  class="cursor-pointer text-[#007bff] hover:text-[#0056b3] text-xs/6 font-bold"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <span class="">{{ shopUrl }}</span>
                </a>
                <div class="text-sm/6 break-words text-wrap text-purple-900">{{
                    settings['settings.seo.page_title']
                  }}
                </div>
                <span class="text-sm/6 text-wrap break-words">{{ settings['settings.seo.meta_description'] }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, watch} from 'vue';
import FeatureTitle from '@/components/dashboard/FeatureTitle.vue';
import {useCompany} from '@/composables/useCompany';
import {useDefaultMeta} from '@/composables/useDefaultMeta';

const {company, companyMetaForm} = useCompany();

const companySlug = computed(() => {
  return company.value.slug;
});

const companyName = computed(() => {
  return company.value.name;
});

const shopUrl = computed(() => {
  return `https://${companySlug.value}.fleetwire.io`;
});

const {state: settings, updateDefaultMeta} = useDefaultMeta({
  'settings.seo.page_title': {type: 'string', default: ''},
  'settings.seo.meta_description': {type: 'string'},
});

watch(() => company.value, (newCompany) => {
    if (newCompany?.name) {
      if (!settings['settings.seo.page_title']) {
        settings['settings.seo.page_title'] = newCompany.name;
      }
    }
  },
  {immediate: true}
);
</script>